import moment, { type Moment } from "moment/moment";
import Tooltip from "../UI/Tooltip";
import Status from "../UI/Status";
import Text from "../UI/Text";
// @ts-expect-error pure js
import { getLastAnniversaryDate, getNextAnniversaryDate } from "../../utils";
import type { Tenant } from "../../models/tenant";
import { formatTypeIndice } from "../../models/property";

interface RevisionStatus {
  done: boolean;
  cannotRevise: boolean;
  lastRevisionDate?: string | null;
  nextRevisionDate?: string;
}

function getRevisionStatusTriennal(tenant: Tenant): RevisionStatus {
  const debut = tenant?.debut;
  const lastRevisionDate = tenant?.last_revision_date;

  const now = moment();
  const debutMoment = moment(debut);
  const lastRevision = lastRevisionDate ? moment(lastRevisionDate) : null;

  const referenceDate = lastRevision ?? debutMoment;
  const nextRevisionDate = referenceDate.clone().add(3, "years");

  const canReviseNow = now.isSameOrAfter(nextRevisionDate, "day");

  return {
    done: !canReviseNow,
    cannotRevise: false,
    lastRevisionDate: lastRevision?.format("DD/MM/YYYY") ?? null,
    nextRevisionDate: nextRevisionDate.format("DD/MM/YYYY"),
  };
}

function getRevisionStatus(tenant: Tenant): RevisionStatus {
  if (tenant?.is_triennal) {
    return getRevisionStatusTriennal(tenant);
  }

  const debut = tenant?.debut;
  const lastRevisionDate = tenant?.last_revision_date;

  if (!debut) {
    return { done: false, cannotRevise: true };
  }

  const now = moment();
  const lastAnniversary: Moment = getLastAnniversaryDate(debut);
  const nextAnniversary: Moment = getNextAnniversaryDate(debut);

  const tenantAgeInYears = now.diff(debut, "years");
  if (tenantAgeInYears < 1) {
    return {
      done: false,
      cannotRevise: true,
      lastRevisionDate: lastRevisionDate
        ? moment(lastRevisionDate).format("DD/MM/YYYY")
        : null,
      nextRevisionDate: nextAnniversary.format("DD/MM/YYYY"),
    };
  }

  const revisionDoneThisYear =
    !!lastRevisionDate &&
    moment(lastRevisionDate).isSameOrAfter(lastAnniversary);

  return {
    done: revisionDoneThisYear,
    cannotRevise: false,
    lastRevisionDate: lastRevisionDate
      ? moment(lastRevisionDate).format("DD/MM/YYYY")
      : null,
    nextRevisionDate: nextAnniversary.format("DD/MM/YYYY"),
  };
}

export default function RevisionTag({ tenant }: { tenant: Tenant }) {
  const status = getRevisionStatus(tenant);

  const tooltipContent = (
    <>
      <Text>
        <Text as="strong" weight="bold">
          Dernière révision :
        </Text>{" "}
        {status.lastRevisionDate ?? "Jamais"}
      </Text>
      <Text>
        <Text as="strong" weight="bold">
          Prochaine date anniversaire :
        </Text>{" "}
        {status.nextRevisionDate ?? "-"}
      </Text>
    </>
  );

  if (status.cannotRevise || status.done) {
    return null;
  }

  const label = tenant?.type_irl ? formatTypeIndice(tenant.type_irl) : "IRL";

  return (
    <Tooltip content={tooltipContent}>
      <Status color="warning" text={label} />
    </Tooltip>
  );
}
